import React, { Component } from "react";
import styled from "styled-components";
import Page from "../../components/Page";
import { Button } from "../../components/buttons";
import toasts from "../../services/toasts";
import api from "../../services/api";

const SearchForm = styled.form`
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
  margin-bottom: 16px;
`;

const SearchInput = styled.input`
  width: 320px;
  max-width: 100%;
  padding: 10px 12px;
  border-radius: 8px;
`;

const HelperText = styled.p`
  margin: 0 0 20px;
  color: var(--muted-text);
  max-width: 760px;
  line-height: 1.5;
`;

const ResultsTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  border-spacing: 0;

  th,
  td {
    border: 1px solid var(--border-color);
    padding: 10px 12px;
    text-align: left;
    vertical-align: middle;
  }

  th {
    background: var(--surface-bg);
  }
`;

const InlineGrant = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  flex-wrap: wrap;
`;

const AmountInput = styled.input`
  width: 110px;
  padding: 8px 10px;
  border-radius: 8px;
`;

const EmptyState = styled.div`
  padding: 28px 16px;
  border: 1px dashed var(--border-color);
  border-radius: 12px;
  color: var(--muted-text);
  background: var(--surface-bg);
`;

const StatusText = styled.p`
  margin: 0 0 16px;
  color: var(--muted-text);
`;

class Tokens extends Component {
  state = {
    query: "",
    results: [],
    searched: false,
    loading: false,
    grantingUserId: null,
    grantAmounts: {}
  };

  handleSearch = async event => {
    if (event) {
      event.preventDefault();
    }

    const query = this.state.query.trim();
    if (!query) {
      this.setState({ results: [], searched: false });
      return;
    }

    this.setState({ loading: true });
    try {
      const results = await api.meetup_credits.searchUsers({ query });
      this.setState({ results, searched: true, loading: false });
    } catch (err) {
      const message =
        err.response && err.response.data && err.response.data.message
          ? err.response.data.message
          : err.toString();
      toasts.error(message);
      this.setState({ loading: false });
    }
  };

  setGrantAmount = (user_id, value) => {
    this.setState(state => ({
      grantAmounts: {
        ...state.grantAmounts,
        [user_id]: value
      }
    }));
  };

  grantTokens = async user => {
    const rawAmount = this.state.grantAmounts[user.user_id];
    const amount = parseInt(rawAmount, 10);

    if (!amount || amount < 1) {
      toasts.error("Enter a token amount greater than 0.");
      return;
    }

    this.setState({ grantingUserId: user.user_id });
    try {
      const updatedUser = await api.meetup_credits.grant({
        user_id: user.user_id,
        amount
      });

      this.setState(state => ({
        grantingUserId: null,
        grantAmounts: {
          ...state.grantAmounts,
          [user.user_id]: ""
        },
        results: state.results.map(item =>
          item.user_id === user.user_id ? { ...item, ...updatedUser } : item
        )
      }));

      toasts.success(
        `Granted ${updatedUser.granted_amount} tokens to ${updatedUser.name} (#${updatedUser.user_id}).`
      );
    } catch (err) {
      const message =
        err.response && err.response.data && err.response.data.message
          ? err.response.data.message
          : err.toString();
      toasts.error(message);
      this.setState({ grantingUserId: null });
    }
  };

  renderResults() {
    const { results, searched, query, loading, grantingUserId, grantAmounts } = this.state;

    if (loading) {
      return <StatusText>Searching users...</StatusText>;
    }

    if (!searched) {
      return (
        <EmptyState>
          Search by user name or exact user ID to load a grantable token balance.
        </EmptyState>
      );
    }

    if (!results.length) {
      return <EmptyState>No users matched "{query}".</EmptyState>;
    }

    return (
      <ResultsTable>
        <thead>
          <tr>
            <th>Name</th>
            <th>User ID</th>
            <th>Phone</th>
            <th>Balance</th>
            <th>Available</th>
            <th>Reserved</th>
            <th>Grant Tokens</th>
          </tr>
        </thead>
        <tbody>
          {results.map(user => (
            <tr key={user.user_id}>
              <td>{user.name || "Unnamed user"}</td>
              <td>{user.user_id}</td>
              <td>{user.phone_number || "n/a"}</td>
              <td>{user.meetup_credits_balance || 0}</td>
              <td>{user.meetup_credits_available || 0}</td>
              <td>{user.meetup_credits_reserved || 0}</td>
              <td>
                <InlineGrant>
                  <AmountInput
                    type="number"
                    min={1}
                    step={1}
                    placeholder="10"
                    value={grantAmounts[user.user_id] || ""}
                    onChange={event =>
                      this.setGrantAmount(user.user_id, event.target.value)
                    }
                  />
                  <Button
                    disabled={grantingUserId === user.user_id}
                    onClick={() => this.grantTokens(user)}
                  >
                    {grantingUserId === user.user_id ? "Granting..." : "Grant"}
                  </Button>
                </InlineGrant>
              </td>
            </tr>
          ))}
        </tbody>
      </ResultsTable>
    );
  }

  render() {
    return (
      <Page title="Tokens">
        <HelperText>
          Search by name or ID, confirm the user details, then add tokens. This
          updates the user&apos;s meetup credit balance.
        </HelperText>

        <SearchForm onSubmit={this.handleSearch}>
          <SearchInput
            type="text"
            placeholder="Search by user name or ID"
            value={this.state.query}
            onChange={event => this.setState({ query: event.target.value })}
          />
          <Button type="submit" disabled={this.state.loading}>
            {this.state.loading ? "Searching..." : "Search"}
          </Button>
        </SearchForm>

        {this.renderResults()}
      </Page>
    );
  }
}

export default Tokens;
